import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client'
import { CombinedGraphQLErrors, ServerError } from '@apollo/client/errors'
import { SetContextLink } from '@apollo/client/link/context'
import { ErrorLink } from '@apollo/client/link/error'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { message } from '@homemaster/jslib'
import { createClient } from 'graphql-ws'

const BUILD_HASH = import.meta.env.VITE_BUILD_HASH ?? 'dev'

const errorLink = new ErrorLink(({ error }) => {
  if (CombinedGraphQLErrors.is(error)) {
    error.errors.forEach(({ message, locations, path }) => {
      console.error(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`,
      )
    })
  }
  if (ServerError.is(error)) {
    if ('statusCode' in error && error.statusCode === 426) {
      message.info('版本已更新，正在刷新...', { autoHide: 2000 })
      setTimeout(() => window.location.reload(), 2000)
      return
    }
    if ('statusCode' in error && error.statusCode === 403) {
      message.error('没有权限进行当前操作')
    }
    console.error(`[Network error]: ${error}`)
  }
})

const authLink = new SetContextLink(({ headers }) => {
  const token = localStorage.getItem('authToken')
  return {
    headers: {
      ...headers,
      Authorization: token ? `Bearer ${token}` : undefined,
      'X-Client-Version': `spa/${BUILD_HASH}`,
    },
  }
})

const wsLink = new GraphQLWsLink(
  createClient({
    url: '/graphql',
    keepAlive: 30_000,
  }),
)

const httpLink = new HttpLink({
  uri: '/graphql',
})

/*
 * Split links based on operation type
 * If the operation is a subscription, use wsLink
 * Otherwise for query/mutation, chain authLink with httpLink
 */
const splitLink = ApolloLink.split(
  ({ query }) => {
    const definition = getMainDefinition(query)
    return (
      definition.kind === 'OperationDefinition' &&
      definition.operation === 'subscription'
    )
  },
  wsLink,
  authLink.concat(httpLink),
)

// Combine links (order matters: auth -> error -> network)
// The network link should be last
const link = ApolloLink.from([errorLink, splitLink])

export const client = new ApolloClient({
  link: link,
  cache: new InMemoryCache({
    possibleTypes: {
      User: ['UserBasic'],
    },
    typePolicies: {
      Query: {
        fields: {
          slotTemplates: {
            // Replace the entire array when writing (don't merge)
            merge(_, incoming) {
              return incoming
            },
          },
        },
      },
      UserBasic: {
        keyFields: ['openid'],
      },
      SiteAdmin: {
        keyFields: ['openid'],
      },
      File: {
        keyFields: ['name'],
      },
    },
  }),
})
